/**
 * Supplier Report (BE-14d-2)
 *
 * Two tables: service providers paid in the range (government fees, fines, add-ons, with what they were paid from) and
 * expense vendors (billed and paid in the range, outstanding now). Delegated work owed to partner centres is not here:
 * it is on Partner Payments.
 * Route: /reports/suppliers
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Grid, TextField, Typography, Link, Alert } from '@mui/material';
import ReportPage, { ReportTable, formatCurrency, formatDate } from '../../components/accounting/ReportPage';
import { financeReportsService } from '../../services/accounting/financeReports.service';
import { toYmd } from '../../utils/accounting/periodUtils';
import { serverMessage } from '../../utils/apiConvert';

const SOURCE_LABELS = {
  TC_BANK: 'Bank', CASH: 'Cash', E_WALLET: 'E-wallet', TC_CREDIT_CARD: 'Credit card', CUSTOMER_CARD: 'Paid by the customer',
};
const money = (v) => (Number(v) ? formatCurrency(v) : '-');
const paidFrom = (sources) => (sources || [])
  .map((s) => `${SOURCE_LABELS[s.method] || s.method} ${formatCurrency(s.amount)}`).join(' · ') || '-';

const SupplierReport = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [from, setFrom] = useState(toYmd(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
  const [to, setTo] = useState(toYmd(new Date()));

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReport(await financeReportsService.getSuppliers({ from, to }));
    } catch (err) {
      setReport(null);
      setError(serverMessage(err, 'Failed to load the supplier report'));
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const pt = report?.providerTotals;
  const vt = report?.vendorTotals;

  const providerColumns = [
    { key: 'name', label: 'Provider' },
    { key: 'payments', label: 'Payments', align: 'right', total: (x) => x.payments },
    { key: 'governmentFees', label: 'Government fees', align: 'right', render: (r) => money(r.governmentFees), total: (x) => formatCurrency(x.governmentFees) },
    { key: 'fines', label: 'Fines', align: 'right', render: (r) => money(r.fines), total: (x) => formatCurrency(x.fines) },
    { key: 'addOns', label: 'Add-ons', align: 'right', render: (r) => money(r.addOns), total: (x) => formatCurrency(x.addOns) },
    { key: 'total', label: 'Total', align: 'right', render: (r) => formatCurrency(r.total), total: (x) => formatCurrency(x.total) },
    { key: 'bySource', label: 'Paid from', render: (r) => paidFrom(r.bySource), total: (x) => paidFrom(x.bySource) },
  ];

  const vendorColumns = [
    { key: 'vendor', label: 'Vendor' },
    { key: 'expenses', label: 'Expenses', align: 'right', total: (x) => x.expenses },
    { key: 'billed', label: 'Billed (range)', align: 'right', render: (r) => formatCurrency(r.billed), total: (x) => formatCurrency(x.billed) },
    { key: 'paid', label: 'Paid (range)', align: 'right', render: (r) => money(r.paid), total: (x) => formatCurrency(x.paid) },
    { key: 'outstanding', label: 'Outstanding (now)', align: 'right', render: (r) => money(r.outstanding), total: (x) => formatCurrency(x.outstanding) },
  ];

  const cards = report ? [
    { label: 'Paid to providers', value: formatCurrency(pt?.total) },
    { label: 'Vendors billed', value: formatCurrency(vt?.billed) },
    { label: 'Vendors paid', value: formatCurrency(vt?.paid), tone: 'good' },
    { label: 'Outstanding now', value: formatCurrency(vt?.outstanding), tone: Number(vt?.outstanding) > 0 ? 'warn' : undefined },
  ] : [];

  return (
    <ReportPage
      title="Supplier Report"
      subtitle={`Service providers and expense vendors, ${formatDate(from)} – ${formatDate(to)}`}
      exportType="suppliers" exportParams={{ from, to }} onError={setError}
      onRefresh={fetchData} error={error} onErrorClose={() => setError(null)}
      loading={loading} cards={cards}
      columns={providerColumns} rows={report?.providers || []} rowKey={(r, i) => r.providerId || `${r.name}-${i}`}
      totals={pt} totalsLabel="TOTAL"
      emptyMessage="No provider was paid in this range."
      filters={(
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} sm={3}>
            <TextField fullWidth size="small" label="From" type="date" value={from}
              onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth size="small" label="To" type="date" value={to}
              onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
        </Grid>
      )}
      footer={(
        <>
          <ReportTable
            title="Expense vendors"
            columns={vendorColumns} rows={report?.vendors || []} rowKey={(r, i) => `${r.vendor}-${i}`}
            totals={vt} totalsLabel="TOTAL" emptyMessage="No expense in this range."
          />
          <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
            <Typography sx={{ fontSize: '0.75rem' }}>
              Work delegated to partner centres is not listed here — see{' '}
              <Link component={RouterLink} to="/accounting/partner-payments">Partner Payments</Link> for what you owe them.
            </Typography>
          </Alert>
        </>
      )}
    >
      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, mb: 1 }}>Service providers</Typography>
    </ReportPage>
  );
};

export default SupplierReport;
